import { DetailedBenefit } from '@/lib/schema/dealer/types';
import clsx from 'clsx';
import React from 'react';

const MAX_TITLE_LENGTH = 40;
const MAX_BODY_LENGTH = 280;

interface ReadonlyDetailedBenefitsProps {
  detailedBenefits: DetailedBenefit[];
}

export const ReadonlyDetailedBenefits: React.FC<
  ReadonlyDetailedBenefitsProps
> = ({ detailedBenefits }) => {
  return (
    <div className="flex flex-col gap-l ml:flex-row ml:justify-between ml:gap-xl">
      {detailedBenefits.map((perk) => (
        <div key={perk.title} className="flex flex-col gap-m p-m">
          <div className="text-center text-body1Medium text-brandMidnight">
            {perk.title}
          </div>
          <div className="text-center text-body2Light text-neutral-900">
            {perk.body}
          </div>
        </div>
      ))}
    </div>
  );
};

const HoverLabel: React.FC<{
  label: string;
  charCount: number;
  maxChars: number;
}> = (props) => {
  return (
    <div className="absolute left-0 top-0 hidden -translate-y-full bg-red-medium p-xs leading-[9px] text-white peer-hover:block peer-focus:block">
      <span className="text-[10px]">{props.label}</span>{' '}
      <span className="text-[9px] font-light">
        {props.charCount}/{props.maxChars} characters
      </span>
    </div>
  );
};

interface EditableDetailedBenefitsProps {
  detailedBenefits: DetailedBenefit[];
  setDetailedBenefits: (detailedBenefits: DetailedBenefit[]) => void;
}

export const EditableDetailedBenefits: React.FC<
  EditableDetailedBenefitsProps
> = ({ detailedBenefits, setDetailedBenefits }) => {
  const inputStyle =
    'peer w-full border border-dashed border-transparent bg-transparent p-s text-center decoration-red-medium hover:border-red-medium hover:underline focus:border-red-medium focus:no-underline';

  return (
    <div className="flex flex-col ml:flex-row ml:justify-between ml:gap-xl">
      {detailedBenefits.map((perk, i) => (
        <div key={i} className="flex flex-auto flex-col gap-s p-m">
          <div className="relative">
            <input
              placeholder={`Benefit ${i + 1} title`}
              value={perk.title}
              maxLength={MAX_TITLE_LENGTH}
              onChange={(e) =>
                setDetailedBenefits([
                  ...detailedBenefits.slice(0, i),
                  { title: e.target.value, body: detailedBenefits[i].body },
                  ...detailedBenefits.slice(i + 1),
                ])
              }
              className={clsx(
                inputStyle,
                'text-body1Medium text-brandMidnight'
              )}
            />
            <HoverLabel
              label="Title"
              charCount={detailedBenefits[i].title.length}
              maxChars={MAX_TITLE_LENGTH}
            />
          </div>
          <div className="relative">
            <textarea
              placeholder={`Benefit ${i + 1} description`}
              value={perk.body}
              maxLength={MAX_BODY_LENGTH}
              onChange={(e) =>
                setDetailedBenefits([
                  ...detailedBenefits.slice(0, i),
                  { title: detailedBenefits[i].title, body: e.target.value },
                  ...detailedBenefits.slice(i + 1),
                ])
              }
              className={clsx(
                inputStyle,
                'min-h-[80px] text-body2Light text-neutral-900 ml:min-h-[140px]'
              )}
            />
            <HoverLabel
              label="Description"
              charCount={detailedBenefits[i].body.length}
              maxChars={MAX_BODY_LENGTH}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
